/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import styled from 'styled-components';
import { useReactToPrint } from 'react-to-print';
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { ReportTableType, getDailyInspectionReport, getFormsReport } from '../../../api/reports';
import { ButtonWithLoader, InputBaseContainer, InputSelect } from '../../../molecules';
import DashboardLayout from '../../../layouts/DashboardLayout';
import { labels, selectStyles } from './InspectorReportSchema';
import { ButtonMain, DatePicker } from '../../../atoms';
import { DATETIME_FORMAT, DEFAULT_DATE_FORMAT, GET_ALL_QUERY_PARAMS } from '../../../constants';
import { getUsers } from '../../../api/users';
import { USER_TYPES_VALUES } from '../../../constants/forms';
import { RootState } from '../../../redux/root';
import { setPermitInspectionsList } from '../../../redux/slices/inspectionsSlice';
import MobileRecord from '../../Responsive/MobileRecord';
import { formatDateTime } from '../../../utils/formatDate';
import PrintRecrod from './PrintRecrod';
import InspectionFormPopup from './InspectionFormPopup';
import { setRecords, setReportsFormLoader, setShowFormPopup } from '../../../redux/slices/InspectionsReportSlice';
// Sample at /forms/GenerateEmployeeReportForm.tsx
const InspectorReports = () => {
  const { errors, control, setError, trigger, handleSubmit, watch } = useForm<ReportTableType>({});
  const [isFetching, setIsFetching] = useState(false);
  const [inspectorsData, setInspectorsData] = useState([]);
  const [disabledSubmit, setDisabledSubmit] = useState(true);
  const printRef = React.useRef<HTMLDivElement>();
  const permitInspectionsList = useSelector((s: RootState) => s.inspections.permitInspectionsList);
  const dispatch = useDispatch();
  const selectedReport = watch('report');
  const formsDate = watch('formsDate');
  const inspector: any = watch('inspector');

  useEffect(() => {
    const bool1 = inspector && inspector.id;
    const bool2 = selectedReport && selectedReport.value;
    const bool3 = formsDate && typeof formsDate === 'object';
    setDisabledSubmit(!(bool1 && bool2 && bool3));
  }, [selectedReport, formsDate, inspector]);

  const handlePrintDailyTable = useReactToPrint({
    content: () => printRef.current,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [submitClicked, setSubmitClicked] = useState<boolean>(false);
  React.useEffect(() => {
    setIsFetching(true);
    getUsers({
      ...GET_ALL_QUERY_PARAMS,
      userRole: USER_TYPES_VALUES.INSPECTOR,
    })
      .then(response => {
        setInspectorsData(response.data.results);
      })
      .catch(() => {})
      .finally(() => setIsFetching(false));

    return () => {
      dispatch(setPermitInspectionsList([]));
    };
  }, []);

  const dailyReportItem = [
    {
      value: 'dailyInspectionReport',
      label: 'Daily Inspection Report',
    },
  ];

  const onSubmit = data => {
    setSubmitClicked(true);
    const fromDate = format(data.formsDate, 'MM/dd/yyyy');
    const params = {
      fromDate,
      toDate: fromDate,
      inspector: data.inspector.id,
    };
    const loadData = async () => {
      const result = await getDailyInspectionReport(params);
      dispatch(setPermitInspectionsList(result.data.data));
    };
    loadData();
  };
  const inspectionForm = async () => {
    try {
      dispatch(setShowFormPopup(true));
       const payload = {
        inspector: inspector.id,
        date: formatDateTime(formsDate, 'MM/dd/yyyy'),
       };
       const results = await getFormsReport(payload);

       // The endpoint used to return a bare array and now returns
       // { forms, skipped }. Read both shapes so this page keeps working
       // whichever of the API and the UI deploys first — mapping the envelope
       // directly throws, and the catch below would swallow it and strand the
       // loader on screen forever.
       // @ts-ignore
       const forms = Array.isArray(results.data) ? results.data : results.data?.forms || [];

       // `id` is the array INDEX here, not the inspection id: this page's popup
       // looks a selected form up as `records[item]`, so the ids have to index
       // the array. The desktop modal keys off the real inspection id instead.
       const recordsNewIds = forms.map((item, index) => ({
        ...item,
        id: index,
       }));
       dispatch(setRecords(recordsNewIds));
       dispatch(setReportsFormLoader(false));
    } catch (e) {
      // Never leave the popup spinning: without this the loader stays up with
      // no records and no message, which is indistinguishable from a hang.
      dispatch(setReportsFormLoader(false));
    }
  };

  const data = [
    { label: 'Project Name', key1: 'projectName' },
    { label: 'Permit Name', key1: 'permitName' },
    { label: 'Address', key1: 'projectAddress' },
    { label: 'Information', key1: 'information' },
    { label: 'Permit Number', key1: 'permit' },
    { label: 'Inspection ID', key1: 'id' },
    { label: 'Inspection Type', key1: 'activityTypeName' },
    { label: 'Project Manager', key1: 'managerFirstName', key2: 'managerLastName' },
    { label: 'Jurisdiction', key1: 'jurisdiction' },
    { label: 'AM/PM', key1: 'period' },
    { label: 'Contact', key1: 'contactFirstName', key2: 'contactLastName' },
    { label: 'Contact Details', key1: 'phoneNumber' },
  ];
  return (
    <DashboardLayout NoSubMenu>
      <Page>
        <Card>
          <SectionTitle>Inspector Reports</SectionTitle>
          <FormContainer autoComplete="off" aria-autocomplete="none" onSubmit={handleSubmit(onSubmit)}>
            <InputBaseContainer>
              <Controller
                name="report"
                defaultValue=""
                control={control}
                render={props => (
                  <InputSelect
                    placeholder="Select report"
                    isSearchable={false}
                    styles={selectStyles}
                    error={errors.report}
                    options={dailyReportItem}
                    isRequired
                  // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                  />
                )}
              />
            </InputBaseContainer>
            <InputBaseContainer>
              <DateWrapper>
                <Controller
                  name="formsDate"
                  defaultValue=""
                  control={control}
                  render={({ value, onChange }) => (
                    <DatePicker
                      disabled={isFetching}
                      onChange={onChange}
                      dateFormat={DEFAULT_DATE_FORMAT}
                      selected={value}
                      placeholderText="Select Date"
                      required
                    />
                  )}
                />
              </DateWrapper>
            </InputBaseContainer>
            <InputBaseContainer>
              <Controller
                name="inspector"
                defaultValue=""
                control={control}
                render={props => (
                  <InputSelect
                    isSearchable={false}
                    styles={selectStyles}
                    isLoading={isFetching}
                    isDisabled={isFetching}
                    error={errors.inspector}
                    getOptionValue={(item: any) => item.id || Math.random()}
                    getOptionLabel={(item: any) => `${item?.firstName} ${item?.lastName}`}
                    placeholder={isFetching ? '' : 'Select Inspector'}
                    options={inspectorsData}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                  />
                )}
              />
            </InputBaseContainer>
            <ButtonContainer>
              <ButtonMain
                type="submit"
                disabled={disabledSubmit}
              >
                Submit
              </ButtonMain>
              <ButtonWithLoader
                variant="outlined"
                loading={isLoading}
                disabled={permitInspectionsList.length === 0}
                onClick={handlePrintDailyTable}
              >
                Export to PDF
              </ButtonWithLoader>
              <ButtonWithLoader
                disabled={permitInspectionsList.length === 0}
                type="submit"
                variant="outlined"
                loading={isLoading}
                onClick={inspectionForm}
              >
                Inspection Form
              </ButtonWithLoader>
            </ButtonContainer>

          </FormContainer>
        </Card>
        <InspectionFormPopup />
        <ReportBody>
          {((permitInspectionsList.length > 0 && inspector) || submitClicked) && (
            <Card>
              <PrintArea ref={printRef}>
                <TableWrapper>
                  <Title>Daily Inspection Report</Title>

                  <List>
                    <li>
                      <Bold>Inspector: </Bold> {`${inspector.firstName} ${inspector.lastName}`}
                    </li>
                    <li>
                      <Bold>Inspection Date: </Bold>
                      {formatDateTime(formsDate, 'MM/dd/yyy')}
                    </li>
                    <li>
                      <Bold>Report Date: </Bold>
                      {formatDateTime(new Date(), DATETIME_FORMAT)}
                    </li>
                    <li>
                      <Bold>Total Inspections: </Bold>
                      {permitInspectionsList.length}
                    </li>
                  </List>
                </TableWrapper>
                <PrintRecrod rows={permitInspectionsList} />
                {permitInspectionsList.map((item, index) => (
                  <PrintHide>
                    <MobileRecord fields={data} item={item} key={item.id} shaded={index % 2 === 0} />
                  </PrintHide>
                ))}
              </PrintArea>
            </Card>
          )}

          {!submitClicked && (
            <Card>
              <NoRecords>Please, select Report from top menu and click on «Submit» button.</NoRecords>
            </Card>
          )}
        </ReportBody>
      </Page>
    </DashboardLayout>
  );
};

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const Page = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 20px 16px 0;
  background: var(--defaultBackground);
  font-family: ${FONT_STACK};

  @media only screen and (max-width: 840px) {
    padding: 12px 12px 0;
  }

  @media only screen and (max-width: 540px) {
    padding: 10px 10px 0;
  }
`;

const Card = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 24px;
  margin-bottom: 20px;
  font-family: ${FONT_STACK};

  @media only screen and (max-width: 840px) {
    padding: 14px;
    margin-bottom: 12px;
  }

  @media only screen and (max-width: 540px) {
    padding: 12px;
  }
`;

const SectionTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  margin-bottom: 20px;
`;

const PrintArea = styled.div`
  margin: 0;
`;

const NoRecords = styled.div`
  margin: 0 auto;
  color: var(--secondaryText);
  text-align: center;
`;

const PrintHide = styled.div`
  @media print {
    display: none;
  }
`;

const ReportBody = styled.div`
  flex-grow: 1;
`;

const FormContainer = styled.form`
  display: flex;
  position: relative;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  > div {
    width: auto;
    padding: 0;
    margin: 0;
  }

  @media only screen and (max-width: 540px) {
    gap: 10px;

    > div {
      width: 100%;
    }
  }
`;

const DateWrapper = styled.div`
  input {
    cursor: pointer;
    width: 180px;
    height: 40px;
    border-radius: 8px;
    border: 1px solid var(--border);
    padding: 0 14px;
    font-size: 0.875rem;
    color: var(--mainText);
    background: var(--neutral-0);
    font-family: ${FONT_STACK};
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    box-sizing: border-box;

    &::placeholder {
      color: var(--neutral-500);
      font-size: 0.875rem;
    }

    &:focus {
      outline: none;
      border-color: var(--primary-600);
      box-shadow: var(--shadow-focus);
    }
  }

  @media only screen and (max-width: 540px) {
    width: 100%;

    input {
      width: 100%;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;

  .MuiButton-root {
    text-transform: none;
    letter-spacing: 0.01em;
    border-radius: 8px;
    height: 40px;
    min-width: 140px;
    font-weight: 600;
    font-size: 0.875rem;
    font-family: ${FONT_STACK};
  }

  @media only screen and (max-width: 1025px) {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
  }
`;

const TableWrapper = styled.div``;

const Bold = styled.span`
  color: var(--mainText);
  font-weight: bold;
`;
const Title = styled.h1`
  margin: 0;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  li {
    padding: 8px 0;
    font-size: 18px;
    color: var(--primary);
  }
`;
export default InspectorReports;
